import { stableStringify as stringify } from "src/app/helpers/stable-stringify.helper";
import { Md5 } from "ts-md5";
import { ChartsFieldsGroup } from "../charts/charts.model";
import { ModelVersionData } from "../models/state/model";

export const CategoryBuilderDebounceTime = 500;
export const HumanTypingDebounceTime = 350;

interface StructuredCount {
  [propertyName: string]: number;
}

export interface FilterFacetCounts {
  lemma_count: number;
  token_count: number;
  ngram_count: number;
  entity_count: number;
  phrase_group_count: number;
  phrase_count: number;
  emitter_role_count: number;
  emitter_count: number;
  effort_count: number;
  fluff_count: number;
  segment_count: number;
  token_dependency_count: number;
  structured_data_counts: StructuredCount;
  msg_structured_data_counts: StructuredCount;
}

export interface FilterFacet {
  type: OperandType;
  count: number;
  structuredField?: string;
}

export enum OperandType {
  LEMMA = "lemma",
  TOKEN = "token",
  ENTITY = "entity",
  NGRAM = "ngram",
  QUERY = "query",
  PHRASE_GROUP = "phrase_group",
  PHRASE = "phrase",
  STRUCTURED_FIELD = "structured_field",
  MSG_STRUCTURED_FIELD = "msg_structured_field",
  CATEGORY_REFERENCE = "category_reference",
  UNCATEGORIZED = "uncategorized",
  TOKEN_DEPENDENCY = "token_dependency",
  RANGE = "range",
  EMITTER_ROLE = "emitter_role",
  EMITTER = "emitter",
  EFFORT = "effort",
  FLUFF = "fluff",
  SEGMENT = "segment",
}

export enum OperatorType {
  AND = "and",
  OR = "or",
  NOT = "not",
}

export enum RangeType {
  SENTIMENT = "sentiment",
  CUMULATIVE_EFFORT_VALUE = "cumulative effort",
}

export enum RangeOperator {
  EQUAL = "=",
  LESS_THAN = "<",
  LESS_THAN_OR_EQUAL = "<=",
  GREATER_THAN = ">",
  GREATER_THAN_OR_EQUAL = ">=",
}

export interface Expression {
  type: OperatorType | OperandType;

  negate?: boolean;

  // Operators
  id?: string;
  label?: string;
  children?: Expression[];

  // Operands
  value?: string;
  frequency?: number;
  sentiment?: number;

  // Phrase group operands
  phrases?: string[];

  // Structured fields
  structured_field?: string;
  message_specific?: boolean;

  // Numeric ranges
  range_field?: string;
  range_operator?: RangeOperator;
  range_operand?: number;

  // Uncategorized
  hashes?: string[];

  // Visibility
  hidden?: boolean;
}

export interface PresentationalRecommendation extends Expression {
  literal: boolean;
  score?: number;
  disabled: boolean;
}

export interface ExpressionLocation {
  parentID: string;
  parentIndex: number;
}

export enum CursorType {
  Edit = "Edit",
  Insert = "Insert",
}

export interface CursorLocation extends ExpressionLocation {
  type: CursorType;
}

export enum PresentationalNodeType {
  Expression = "Expression",
  RootCursor = "Root Cursor",
  Cursor = "Cursor",
}

export interface Lineage {
  id: string;
  visible: boolean;
  head: boolean;
  tail: boolean;
  tag: boolean;
  operatorType?: OperatorType;
}

export class PresentationalNode {
  // Presentational nodes can either represent an expression or a cursor
  type: PresentationalNodeType;

  // We use hashes for trackby
  hash: string;

  // Items for easier presentation/interaction
  parentage: Lineage[];
  parentOperator: OperatorType;
  expandable: boolean;
  expanded: boolean;
  location: ExpressionLocation | CursorLocation;
  firstPresentedChild: boolean;
  lastPresentedChild: boolean;
  draggable: boolean;

  // The expression this node represents
  expression?: Expression;

  constructor(args: {
    type: PresentationalNodeType;
    parentage: Lineage[];
    parentOperator: OperatorType;
    expandable: boolean;
    expanded: boolean;
    location: ExpressionLocation;
    firstPresentedChild: boolean;
    lastPresentedChild: boolean;
    draggable: boolean;
    expression?: Expression;
  }) {
    Object.assign(this, args);
    const {
      type,
      parentage,
      parentOperator,
      expandable,
      expanded,
      location,
      firstPresentedChild,
      lastPresentedChild,
      draggable,
      expression,
    } = args;

    // Compute the hash
    const hashableContent = {
      type,
      parentage,
      parentOperator,
      expandable,
      expanded,
      location,
      firstPresentedChild,
      lastPresentedChild,
      draggable,
      ...expression,
    };

    // Children should not be part of the hash
    delete hashableContent.children;

    this.hash = Md5.hashStr(stringify(hashableContent));
  }
}

export interface Recommendations {
  lemma: Expression;
  token: Expression;
  entity: Expression;
  ngram: Expression;
  phrase: Expression;
  phrase_group: Expression;
  emitter_role: Expression;
  emitter: Expression;
  effort: Expression;
  fluff: Expression;
  segment: Expression;
  token_dependency: Expression;
  structured_fields: { [field_name: string]: Expression };
  msg_structured_fields: { [field_name: string]: Expression };
  valid_query: boolean;
}

export interface PaginatedHits {
  total: {
    value: number;
    relation?: string;
  };
  max_score?: number;
  hits: PaginatedHit[];
}

export interface SentencesResults {
  verbatims: number;
  sentences: number;
  paginated_hits: PaginatedHits;
}

export interface PaginatedHit {
  index: string;
  type: string;
  id: string;
  _score: number;
  source: any;
}

export enum SortBy {
  FREQUENCY = "frequency",
  ALPHABETICAL = "alphabetical",
  SENTIMENT = "sentiment",
  RECOMMENDED = "recommended",
  MISSING = "missing",
  UNCATEGORIZED = "uncategorized",
}

export enum SentimentRangeType {
  POSITIVE = "positive",
  NEGATIVE = "negative",
  NEUTRAL = "neutral",
  CUSTOM = "custom",
  DEFAULT = "default",
}
export interface SentimentRange {
  type: SentimentRangeType;
  min: number;
  max: number;
  minInclusive: boolean;
  maxInclusive: boolean;
}

export const DefaultSentimentRange: SentimentRange = {
  type: SentimentRangeType.DEFAULT,
  max: 5,
  maxInclusive: true,
  min: -5,
  minInclusive: true,
};

export const PositiveSentimentRange: SentimentRange = {
  type: SentimentRangeType.POSITIVE,
  max: 5,
  maxInclusive: true,
  min: 0,
  minInclusive: false,
};

export const NeutralSentimentRange: SentimentRange = {
  type: SentimentRangeType.NEUTRAL,
  max: 0,
  maxInclusive: true,
  min: 0,
  minInclusive: true,
};

export const NegativeSentimentRange: SentimentRange = {
  type: SentimentRangeType.NEGATIVE,
  max: 0,
  maxInclusive: false,
  min: -5,
  minInclusive: true,
};

export interface CategoryModelFolder {
  id: string;
  label: string;
  children?: Expression[];
  type?: OperatorType;
}

export interface FlatCategoryModelFolder extends CategoryModelFolder {
  level: number;
}

export interface FlatExpression extends Expression {
  level: number;
}

export interface CategoryModelVersionApplication {
  data_source_id: string;
  model_version_id: string;
  sentiment_version_id: string;
  application_data: CategoryHashes;
  category_sentence_hits: {
    [categoryHash: string]: number;
  };
  category_verbatim_hits: {
    [categoryHash: string]: number;
  };
  categorized_verbatims: number;
  categorized_sentences: number;
  co_occurrences: [
    {
      expressionA: string;
      expressionB: string;
      count: number;
    },
  ];
}

export interface CategoryHashes {
  [categoryID: string]: string;
}

export interface CategoryLookup {
  [categoryID: string]: Expression;
}

export interface CategoryHits {
  totalSentences: number;
  totalVerbatims: number;
  coOccurrences: {
    category: Expression;
    coOccurrences: number;
  }[];
  dirty: boolean;
}

export enum InputPlaceholders {
  FOLDER = "NEW FOLDER INPUT",
  CATEGORY = "NEW CATEGORY INPUT",
}

export interface CategoryPlaceholder extends Expression {
  parentID?: string;
  level?: number;
}

export interface GroupExpanded {
  [groupId: string]: boolean;
}

export interface CategoryBuilderHistoryObj {
  sandbox: Expression;
  workingVersionData: ModelVersionData;
}

export interface DeltaModeStateObj {
  animateIcons: boolean;
  deltaOriginalExpression: Expression;
  deltaCurrentExpression: Expression;
  deltaPlusExpression: Expression;
  deltaMinusExpression: Expression;
}

export interface DeltaStats {
  sentence_plus_count: number;
  sentence_minus_count: number;
  verbatim_plus_count: number;
  verbatim_minus_count: number;
}

export enum SpecialCategoryLabels {
  SANDBOX = "Sandbox",
  UNCATEGORIZED = "Uncategorized",
}

export interface TransientAttribute {
  name: string;
  value: string;
}

export interface TransientData {
  document_id: string;
  translate: boolean;
  verbatim_set: TransientAttribute[];
  attribute_set: TransientAttribute[];
}

export interface CategorizeSumupCategoryHit {
  category_id: string;
  category_name?: string;
  sentence: string;
  highlights?: { start: number; end: number }[];
}

export interface CategorizeSumupCategory {
  id: string;
  name: string;
  category_hits?: CategorizeSumupCategoryHit[];
  children?: CategorizeSumupCategory[];
}

export interface CategorizeSumupTranslation {
  original?: string;
  language?: string;
  translated: string;
  sentLen?: { srcSentLen: number[]; transSentLen: number[] };
}

export interface CategorizeSumupVerbatim {
  name: string;
  translated?: CategorizeSumupTranslation;
  categories: CategorizeSumupCategory[];
}

export interface CategorizeSumupDocument {
  document_id: string;
  sentiment?: number;
  verbatim_set: CategorizeSumupVerbatim[];
}

export interface CategorizeSumupErrorDetails {
  code: number;
  reason?: string;
  description?: string;
  suggestion?: string;
  trace?: string;
}

export interface CategorizeSumupError {
  message?: string;
  service?: string;
  trigger?: string;
  details?: CategorizeSumupErrorDetails;
}

export interface ExplainOperatorNode {
  type: OperatorType;
  id: string;
  label?: string;
}

export interface CategorizeSumupRuleHit {
  category_id: string;
  category_name: string;
  expression: Expression;
  matched_sentences: string[];
  operator_path?: ExplainOperatorNode[];
  negated?: boolean;
  contributed?: boolean;
}

export interface ExplainTreeLeaf {
  kind: "leaf";
  hit: CategorizeSumupRuleHit;
  depth: number;
  lineage: Lineage[];
  isLastSibling: boolean;
}

export interface ExplainTreeOperator {
  kind: "operator";
  id: string;
  type: OperatorType;
  label?: string;
  children: ExplainTreeNode[];
  hasContributing: boolean;
  depth: number;
  lineage: Lineage[];
  isLastSibling: boolean;
  hasChildren: boolean;
}

export type ExplainTreeNode = ExplainTreeOperator | ExplainTreeLeaf;

export interface ExplainCategoryTree {
  category_id: string;
  category_name: string;
  roots: ExplainTreeNode[];
  hasNegated: boolean;
  matchCount: number;
  contributedCount: number;
  branchCount: number;
}

/**
 * Flat presentation rows for the matched-rules tree. The explain forest is
 * flattened preorder into a single list where every node (and every matched
 * sentence) is one row carrying its own complete ancestor `lineage`. This
 * mirrors how ``ExpressionService.present()`` flattens the editable category
 * tree, so the two views render pixel-identical lineage connectors.
 */
export interface ExplainOperatorRow {
  kind: "operator";
  id: string;
  type: OperatorType;
  label?: string;
  lineage: Lineage[];
  expandable: boolean;
  expanded: boolean;
  hasContributing: boolean;
}

export interface ExplainLeafRow {
  kind: "leaf";
  id: string;
  hit: CategorizeSumupRuleHit;
  lineage: Lineage[];
}

export interface ExplainSentenceRow {
  kind: "sentence";
  id: string;
  sentence: string;
  lineage: Lineage[];
}

export type ExplainRow =
  | ExplainOperatorRow
  | ExplainLeafRow
  | ExplainSentenceRow;

/**
 * Server-measured breakdown of a categorize sum-up request. Emitted with the
 * same envelope by both engines so the Rule Tracer can compare them; the stage
 * names inside differ because the engines do different work.
 *
 * `request_ms` is the whole request as the router saw it and `engine_ms` only
 * the engine's own execution — the gap between them is `queue_wait_ms`, the
 * wait for an Emperor Penguin worker, which is invisible in any single stage
 * yet routinely dominates when both engines are launched at once.
 */
export interface CategorizeSumupTimings {
  engine?: string;
  request_ms?: number;
  queue_wait_ms?: number;
  engine_ms?: number;
  stages?: Record<string, number>;
  counters?: Record<string, number>;
}

export interface CategorizeSumupResponse {
  result_set?: CategorizeSumupDocument[] | { error: CategorizeSumupError };
  explain?: CategorizeSumupRuleHit[];
  timings?: CategorizeSumupTimings;
  status?: number;
}

export type FilterByList = {
  [key in keyof OperandType]: boolean;
};

export interface FindAndReplaceMatches {
  [key: number]: number | null;
}

export enum SortDirection {
  ASCENDING = "asc",
  DESCENDING = "desc",
}

export interface NotificationCenterNotification {
  message: string;
  color: string;
}

export interface ModelNotificationDetails {
  dirty: boolean;
  duplicateCategoryNames: string[];
  emptyCategoryNames: string[];
}

export interface VerbatimDetailsModal {
  sentence: {
    label: string;
    sentences: PaginatedHit[];
    hashes: CategoryHashes;
    lookup: CategoryLookup;
  };
  verbatim: {
    verbatim: any;
    verbatimId: string;
    categoryNames: any;
  };
  chatAttributes?: {
    chatMsg: any;
    fieldGroups: ChartsFieldsGroup[];
  };
  structuredData: {
    structuredData: any;
    fieldGroups: ChartsFieldsGroup[];
  };
}

export interface GlobalSearchMatch {
  [key: string]: FindAndReplaceMatches[];
}

export interface FindMatchesArguments {
  searchValue: string;
  category?: Expression;
  wholeExpressionEnabled: boolean;
  operandsToFilterBy: string[];
  noFilterByValue: boolean;
  filterByValue: boolean;
}

export interface GettingIndexesOfMatchesArguments {
  presentation: PresentationalNode[];
  noSearchValue: boolean;
  searchValue: string;
  noFilterByValue: boolean;
  filterByValue: boolean;
  operandsToFilterBy: string[];
  wholeExpressionEnabled: boolean;
}
